import { Button, Form, Input, InputNumber, message, Modal, Radio, Select, Typography } from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import { PaymentMethods } from "dtos/application.dto";
import { useEffect, useRef, useState } from "react";
import { TbAlertCircle, TbChevronRight, TbLoader2 } from "react-icons/tb";
import { useNavigate, useSearchParams } from "react-router-dom";

// type PaymentType = "cc" | "ck";

const PaymentCollect = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentMode = searchParams.get("mode") as PaymentMethods;
  const creditor_id = searchParams.get("creditor_id");
  const uuid = searchParams.get("uuid");
  const paynote_user_id = searchParams.get("paynote_user_id");
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setError] = useState("");

  const verifyToken = ApiService.creditor.useVerifyPaymentInfoPageToken();
  const registerPayment = ApiService.creditor.useRegisterPaymentMethods();

  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [plaidPaymentSucessData, setPlaidPaymentSucessData] = useState<{
    lbacc: string;
    source_id: string;
    bank: string;
  }>();
  const [paymentType, setPaymentType] = useState<PaymentMethods>(PaymentMethods.CREDIT_CARD);
  const [token, setToken] = useState<any>(null);
  const [form] = Form.useForm();

  const collectReadyRef = useRef(false);
  const paymentModeRef = useRef(paymentMode);

  useEffect(() => {
    paymentModeRef.current = paymentMode;
  }, [paymentMode]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== "https://paynote-sandbox.seamlesschex.com") return;

      let data = event.data;
      console.log("PLAID", data);
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (!data?.event) return;

      switch (data.event) {
        case "callSuccessFunction":
          setPlaidPaymentSucessData({
            lbacc: data.funding_source.lbacc,
            source_id: data.funding_source.source_id,
            bank: data.funding_source.bank,
          });
          message.success("Bank linked successfully!");
          setIsBankModalOpen(false);
          setMetaModalOpen(true);
          break;
        case "callErrorFunction":
          message.error("Bank linking failed. Please try again.");
          break;
        case "callCloseFunction":
          setIsBankModalOpen(false);
          break;
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);
  console.log({ plaidPaymentSucessData });

  useEffect(() => {
    function setupCollectJS() {
      (window as any).CollectJS.configure({
        variant: "lightbox",
        paymentType: PaymentMethods.CREDIT_CARD,
        fields: {
          ccnumber: { placeholder: "4111 1111 1111 1111" },
          ccexp: { placeholder: "MM / YY" },
          cvv: { placeholder: "123" },
          checkaba: { placeholder: "Routing Number" },
          checkaccount: { placeholder: "Account Number" },
          checkname: { placeholder: "Name on Account" },
        },
        callback: (tokenData: any) => {
          setToken(tokenData);
          setTimeout(() => setMetaModalOpen(true), 120);
          message.success("Payment method captured.");
        },
      });

      collectReadyRef.current = true;

      if (paymentModeRef.current === PaymentMethods.CREDIT_CARD && !isLoading && !isError) {
        (window as any).CollectJS.startPaymentRequest();
      }
    }

    const existingScript = document.getElementById("collectjs");

    if (existingScript) {
      if ((window as any)?.CollectJS) {
        setupCollectJS();
      } else {
        existingScript.addEventListener("load", () => {
          const interval = setInterval(() => {
            if ((window as any)?.CollectJS) {
              clearInterval(interval);
              setupCollectJS();
            }
          }, 50);
        });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "https://seamlesschex.transactiongateway.com/token/Collect.js";
    script.id = "collectjs";
    script.setAttribute("data-tokenization-key", import.meta.env.VITE_APP_SEAMLESS_TOKEN);
    script.async = true;

    script.onload = () => {
      const interval = setInterval(() => {
        if ((window as any)?.CollectJS) {
          clearInterval(interval);
          setupCollectJS(); // ✅ configure + conditionally startPaymentRequest together
        }
      }, 50);
    };

    document.body.appendChild(script);
  }, [isError, isLoading]);

  useEffect(() => {
    if (paymentMode === PaymentMethods.PAYNOTE && !isLoading && !isError) {
      // setSelectModalOpen(true);
      setMetaModalOpen(true);
    } else if (paymentMode === PaymentMethods.ECHECK && !isLoading && !isError) {
      setMetaModalOpen(true);
    } else if (paymentMode === PaymentMethods.PAYNOTE_PLAID && !isLoading && !isError) {
      setIsBankModalOpen(true);
    }
  }, [paymentMode, isError, isLoading]);

  useEffect(() => {
    if (!creditor_id || !uuid || !paymentMode || (!paynote_user_id && paymentMode === PaymentMethods.PAYNOTE_PLAID)) {
      // message.error("Invalid link. Missing parameters.");
      setError("Invalid link. Missing parameters.");
      setIsLoading(false);
      return;
    }
    if (paymentMode === PaymentMethods.CREDIT_CARD || paymentMode === PaymentMethods.ECHECK) {
      setError("Invalid Payment Type.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    verifyToken
      .mutateAsync({ creditor_id: creditor_id, uuid, mode: paymentMode as PaymentMethods })
      .then((data) => {
        if (data.result.found) {
          if (paymentMode === PaymentMethods.PAYNOTE) {
            // setSelectModalOpen(true);
            setMetaModalOpen(true);
          } else if (paymentMode === PaymentMethods.PAYNOTE_PLAID) {
            setIsBankModalOpen(true);
          }
        } else {
          // message.error("Invalid or expired link.");
          setIsLoading(false);
          setError("Invalid or expired link.");
        }
      })
      .catch(() => {
        message.error("Invalid or expired link.");
        setError("Invalid or expired link.");
      })
      .finally(() => setIsLoading(false));
  }, [creditor_id, uuid, paymentMode]);

  function startCollect() {
    if (paymentType === PaymentMethods.ECHECK) {
      setSelectModalOpen(false);
      setMetaModalOpen(true);
      return;
    }
    setIsBankModalOpen(true);
  }
  function renderPaymentPreview() {
    if (paymentType === PaymentMethods.ECHECK) return null;
    if (paymentMode === PaymentMethods.PAYNOTE_PLAID) {
      return (
        <div style={{ padding: 12, marginBottom: 16, borderRadius: 8, background: "#f5f5f5" }}>
          <Typography.Text strong>Card</Typography.Text>
          <br />
          {plaidPaymentSucessData?.bank ?? "BANK"} / {plaidPaymentSucessData?.lbacc}
        </div>
      );
    }
    if (!token) return null;

    if (paymentType === PaymentMethods.CREDIT_CARD) {
      return (
        <div style={{ padding: 12, marginBottom: 16, borderRadius: 8, background: "#f5f5f5" }}>
          <Typography.Text strong>Card</Typography.Text>
          <br />
          {token?.card?.type?.toUpperCase()} / {token?.card?.number}
        </div>
      );
    }

    return null;
  }
  const handleSubmitBankDetails = (values: any) => {
    if (paymentMode === PaymentMethods.PAYNOTE) {
      //call api for paynote
    } else if (paymentMode === PaymentMethods.ECHECK) {
      //call api for check
    }
  };
  function submitMeta(values: any) {
    console.log("here first", paymentMode, token);
    if (paymentMode === PaymentMethods.CREDIT_CARD && !token) return;
    console.log("here second");

    message.warning("Do not close this tab until the form is saved succesfully.");
    console.log({ plaidPaymentSucessData });

    registerPayment.mutate(
      {
        creditor_id: creditor_id!,
        type: paymentMode as any,
        token: paymentMode === PaymentMethods.CREDIT_CARD ? token.token : null,
        routing_number: values.routing_number,
        account_number: values.account_number,
        account_name: values.account_name,
        bank_name: paymentMode === PaymentMethods.PAYNOTE_PLAID ? plaidPaymentSucessData?.bank : values.bank_name,
        bank_type: values.bank_type,
        email: values.email,
        phone: values.phone,
        first_name: values.first_name,
        last_name: values.last_name,
        address: values.address,
        city: values.city,
        state: values.state,
        zip: values.zip,
        uuid: uuid as string,
        paynote_source_id: plaidPaymentSucessData?.source_id,
        paynote_user_id: paynote_user_id as string,
        mask:
          paymentMode === PaymentMethods.CREDIT_CARD
            ? `${token.card.type?.toUpperCase()} / ${token.card.number.slice(-4)}`
            : paymentMode === PaymentMethods.PAYNOTE_PLAID
              ? ` BANK / ${plaidPaymentSucessData?.lbacc}`
              : `BANK / ${String(values.account_number).slice(-4)}`,
      },
      {
        onSuccess: async () => {
          // message.success("Payment method saved.");
          navigate("/payment-method-success");

          // await refetchPaymentInfo();
          // await refetchApplication();

          setMetaModalOpen(false);
          form.resetFields();
        },
      },
    );
  }
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ height: "100vh" }}>
        <TbLoader2 className="animate-spin stroke-purple-600" size={48} />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ height: "100vh" }}>
        <div className="flex flex-col items-center gap-2">
          <TbAlertCircle className="stroke-red-500" size={36} />
          <Typography.Text type="danger">{isError}</Typography.Text>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Paynote: select payment type */}
      <Modal
        closeIcon={null}
        centered
        open={selectModalOpen}
        title="Select Payment Type"
        // onCancel={() => setSelectModalOpen(false)}
        footer={null}
      >
        <Radio.Group
          value={paymentType}
          options={[
            { label: "Continue With Plaid", value: PaymentMethods.CREDIT_CARD },
            { label: "Continue Without Plaid", value: PaymentMethods.ECHECK },
          ]}
          onChange={(val) => setPaymentType(val.target.value)}
        />
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Button type="primary" icon={<TbChevronRight />} onClick={startCollect}>
            Continue
          </Button>
        </div>
      </Modal>

      {/* Confirm details */}
      <Modal
        closeIcon={null}
        open={metaModalOpen}
        title="Confirm Details"
        // onCancel={() => setMetaModalOpen(false)}
        footer={null}
        centered
        // style={{
        //   height: 600,
        //   overflowY: "auto",
        // }}
      >
        {renderPaymentPreview()}
        <Form className="" form={form} onFinish={submitMeta} layout="vertical">
          <div className="h-[600px] overflow-y-auto">
            <Form.Item name="first_name" label="First name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="last_name" label="Last name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
              <Input placeholder="Provide your Paynote customer email or add new" />
            </Form.Item>
            <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
              <InputNumber<string>
                type="tel"
                minLength={14}
                maxLength={14}
                style={{ width: "100%" }}
                formatter={(value) => {
                  if (!value) return "";
                  const onlyNumbers = `${value}`.replace(/\D/g, "");
                  const formatted = onlyNumbers.replace(
                    /^(\d{0,3})(\d{0,3})(\d{0,4})$/,
                    (match, p1, p2, p3) => `${p1 ? `(${p1}) ` : ""}${p2 ? `${p2}` : ""}${p3 ? `-${p3}` : ""}`,
                  );
                  return formatted.trim();
                }}
                parser={(value) => value?.replace(/\D/g, "") as unknown as string}
                controls={false}
              />
            </Form.Item>

            {paymentMode === PaymentMethods.CREDIT_CARD && (
              <>
                <Form.Item name="address" label="Address" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>

                <Form.Item name="city" label="City" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>

                <Form.Item name="state" label="State" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>

                <Form.Item name="zip" label="ZIP Code" rules={[{ required: true }]}>
                  <Input minLength={5} maxLength={5} />
                </Form.Item>
              </>
            )}

            {(paymentMode === PaymentMethods.ECHECK || paymentMode === PaymentMethods.PAYNOTE) && (
              <>
                <Form.Item
                  name="routing_number"
                  label="Routing Number"
                  rules={[
                    { required: true, message: "Routing number is required" },
                    { pattern: /^[0-9]{9}$/, message: "Routing number must be exactly 9 digits" },
                  ]}
                >
                  <Input maxLength={9} inputMode="numeric" />
                </Form.Item>

                <Form.Item
                  name="account_number"
                  label="Account Number"
                  rules={[
                    { required: true, message: "Account number is required" },
                    { pattern: /^[0-9]{6,17}$/, message: "Account number must be 6–17 digits" },
                  ]}
                >
                  <Input maxLength={17} inputMode="numeric" />
                </Form.Item>
                <Form.Item name="account_name" label="Name on Account" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="bank_name" label="Bank Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>

                <Form.Item name="bank_type" label="Account Type" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { label: "Savings", value: "savings" },
                      { label: "Checking", value: "checking" },
                    ]}
                  />
                </Form.Item>
              </>
            )}
          </div>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={registerPayment.isPending}
            disabled={registerPayment.isPending}
          >
            Save Payment Method
          </Button>
        </Form>
      </Modal>

      {/* Paynote bank iframe */}
      <Modal
        open={isBankModalOpen}
        // onCancel={() => setIsBankModalOpen(false)}
        closeIcon={null}
        footer={null}
        centered
        width={500}
        destroyOnClose
      >
        <iframe
          src={`https://paynote-sandbox.seamlesschex.com/#/bank-account/${import.meta.env.VITE_PAYNOTE_PUBLIC_KEY}/${paynote_user_id}`}
          style={{ width: "100%", height: "700px", border: "none", borderRadius: "8px" }}
        />
      </Modal>
    </>
  );
};

export default PaymentCollect;
